import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, Building2 } from 'lucide-react';

interface BankListEditorProps {
  settings: any;
  content: any;
  onSettingsChange: (settings: any) => void;
  onContentChange: (content: any) => void;
}

const defaultBanks = [
  {
    id: "akbank",
    name: "Akbank",
    logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png"
  },
  {
    id: "garanti",
    name: "Garanti BBVA",
    logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/2c60a02b-d37c-48ce-ad8e-deb4e57bf810.jpg"
  },
  {
    id: "yapikredi",
    name: "Yapı Kredi",
    logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png"
  },
  {
    id: "qnb",
    name: "QNB Finansbank",
    logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png"
  },
  {
    id: "isbankasi",
    name: "İş Bankası",
    logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png"
  }
];

const BankListEditor: React.FC<BankListEditorProps> = ({ 
  settings, 
  content, 
  onSettingsChange, 
  onContentChange 
}) => {
  const [newBank, setNewBank] = useState({
    id: '',
    name: '',
    logo: ''
  });
  
  const banks = content?.bankLogos || defaultBanks;
  
  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : type === 'number' 
        ? parseInt(value) 
        : value;
    
    onSettingsChange({ [name]: newValue });
  };
  
  const handleBankChange = (index: number, field: string, value: string) => {
    const newBanks = [...banks];
    newBanks[index] = { ...newBanks[index], [field]: value };
    onContentChange({ bankLogos: newBanks });
  };
  
  const handleAddBank = () => {
    if (newBank.name && newBank.logo) {
      const bankId = newBank.id || newBank.name.toLowerCase().replace(/\s+/g, '-');
      onContentChange({
        bankLogos: [
          ...banks,
          {
            id: bankId,
            name: newBank.name,
            logo: newBank.logo
          }
        ]
      });
      setNewBank({ id: '', name: '', logo: '' });
    }
  };
  
  const handleRemoveBank = (index: number) => {
    const newBanks = [...banks];
    newBanks.splice(index, 1);
    onContentChange({ bankLogos: newBanks });
  };
  
  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Genel Ayarlar</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Başlık
            </label>
            <input
              type="text"
              name="title"
              value={settings?.title || 'Bankalar'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Alt Başlık
            </label>
            <input
              type="text"
              name="subtitle"
              value={settings?.subtitle || 'Çalıştığımız bankalar'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="autoScroll"
              checked={settings?.autoScroll !== false}
              onChange={handleSettingChange}
              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
            />
            <span className="text-sm text-slate-600">Otomatik Kaydırma</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="showNames"
              checked={settings?.showNames !== false}
              onChange={handleSettingChange}
              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
            />
            <span className="text-sm text-slate-600">Banka İsimlerini Göster</span>
          </label>
        </div>
      </div>
      
      {/* Banks */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Bankalar</h3>
        
        <div className="space-y-4">
          {banks.map((bank: any, index: number) => (
            <div key={index} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg">
              <GripVertical size={16} className="text-slate-400 cursor-move" />
              
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                <img 
                  src={bank.logo} 
                  alt={bank.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              
              <div className="flex-1 grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={bank.name}
                  onChange={(e) => handleBankChange(index, 'name', e.target.value)}
                  placeholder="Banka Adı"
                  className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                />
                <input
                  type="text"
                  value={bank.logo}
                  onChange={(e) => handleBankChange(index, 'logo', e.target.value)}
                  placeholder="Logo URL"
                  className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                />
              </div>
              
              <button
                onClick={() => handleRemoveBank(index)}
                className="p-2 text-slate-400 hover:text-red-600 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        
        {/* Add New Bank */}
        <div className="p-4 border border-dashed border-slate-200 rounded-lg space-y-4">
          <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Building2 size={16} className="text-[#F7BA45]" />
            <span>Yeni Banka Ekle</span>
          </h4>
          
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={newBank.name}
              onChange={(e) => setNewBank({ ...newBank, name: e.target.value })}
              placeholder="Banka Adı"
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
            <input
              type="text"
              value={newBank.logo}
              onChange={(e) => setNewBank({ ...newBank, logo: e.target.value })}
              placeholder="Logo URL"
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={handleAddBank}
              disabled={!newBank.name || !newBank.logo}
              className="flex items-center gap-1 px-3 py-1.5 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Plus size={16} />
              <span>Ekle</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankListEditor;